import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, List, ListItemIcon, ListItemButton, ListItemText,
  CircularProgress, Paper, Snackbar, Alert, Menu, MenuItem, Collapse
} from '@mui/material';
import { Logout, PersonAdd, PeopleAlt, Folder, TableChart, Cancel, Info, FeaturedPlayList, ManageHistory, ExpandLess, ExpandMore, MarkAsUnread, MenuBook, CalendarMonth, AdminPanelSettings, Assignment, Analytics, Warehouse } from '@mui/icons-material';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PermContactCalendarIcon from '@mui/icons-material/PermContactCalendar';
import RequestPage from '@mui/icons-material/RequestPage';
import AddToDrive from '@mui/icons-material/AddToDrive';
import Blinds from '@mui/icons-material/Blinds';

import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import bg from '../media/ng.jpg';
import logo from './logo.png';
import CreateAccount from './CreateAccount';
import AllUsers from './AllUsers/AllUsers';
import HelpPage from './HelpPage';
import Dangerous from './Dangerous';
import CheckUsersData from './reports/Reports';
import BDTable from './BDTable/BDTableMenu';
import ConsoleLog from './ConsoleLog';
import MathReport from './mathReports/MathReportMenu';
import TaskManager from './TaskManager';
import TabelRB from './TabelRB';
import EditReportModule from './reports/EditReportModule';
import EditMonthReportModule from './reports/EditMonthReportModule';
import ViewReportModule from './reports/ViewReportModule';
import ViewReportModuleDir from './reports/ViewReportModuleDir';
import UsersPerDay from './reports/CheckUsersOnDay';
import Compare from './reports/CompareModule';
import OFDManagement from './OFDManagement';
import PharmacyMap from '../user/PharmacyMap/index';
import Nomenclature from '../user/Nomenclature';
import AdminDocuments from './AdminDocuments';
import AdminMarkingCodes from './MarkingCodes';
import WhatsAppBots from './WhatsAppBots';
import AdminJournal from './AdminJournal';
import AdminNotifications from './Notifications';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CampaignIcon from '@mui/icons-material/Campaign';

type ReportModule = 'edit' | 'editmonth' | 'editnew' | 'view' | 'empty' | 'compare' | 'MathReport';

// ---------------------------------------------------------------------------
// Shared styles (previously duplicated inline on every ListItemButton)
// ---------------------------------------------------------------------------

const shimmerBefore = {
  content: '""',
  position: 'absolute' as const,
  top: 0,
  left: '-100%',
  width: '100%',
  height: '100%',
  transition: 'left 0.6s',
  zIndex: 1
};

const parentItemSx = {
  color: 'rgba(255, 255, 255, 0.8)',
  borderRadius: '12px',
  mx: 1,
  mb: 0.5,
  px: 1.5,
  py: 0.8,
  minHeight: 48,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    ...shimmerBefore,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)'
  },
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    boxShadow: `
      0 4px 16px rgba(99, 102, 241, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1)
    `,
    transform: 'translateY(-1px)',
    '&::before': { left: '100%' }
  },
  '&:hover:not(.Mui-selected)': {
    background: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    transform: 'translateY(-1px) translateX(2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    backdropFilter: 'blur(10px)',
    '&::before': { left: '100%' }
  },
  '&:hover.Mui-selected': {
    transform: 'translateY(-2px) scale(1.01)',
    boxShadow: `
      0 6px 20px rgba(99, 102, 241, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.2)
    `
  }
};

const collapseSx = {
  background: 'rgba(99, 102, 241, 0.05)',
  borderRadius: '12px',
  mx: 1,
  mt: 0.5,
  border: '1px solid rgba(99, 102, 241, 0.1)'
};

const subItemSx = {
  pl: 2.5,
  pr: 1.5,
  py: 0.6,
  minHeight: 40,
  color: 'rgba(255, 255, 255, 0.7)',
  borderRadius: '8px',
  mx: 1,
  mb: 0.3,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    ...shimmerBefore,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.05), transparent)'
  },
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    color: 'white',
    boxShadow: `
      0 2px 8px rgba(99, 102, 241, 0.4),
      0 0 0 1px rgba(255, 255, 255, 0.1)
    `,
    transform: 'translateX(4px)',
    '&::before': { left: '100%' }
  },
  '&:hover:not(.Mui-selected)': {
    background: 'rgba(255, 255, 255, 0.03)',
    color: 'rgba(255, 255, 255, 0.9)',
    transform: 'translateX(6px)',
    backdropFilter: 'blur(5px)',
    '&::before': { left: '100%' }
  },
  '&:hover.Mui-selected': {
    transform: 'translateX(6px) scale(1.01)',
    boxShadow: `
      0 4px 12px rgba(99, 102, 241, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.2)
    `
  }
};

const actionButtonSx = (from: string, to: string, rgb: string) => ({
  mb: 0.8,
  py: 1,
  borderRadius: '14px',
  background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
  boxShadow: `
    0 4px 12px rgba(${rgb}, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1)
  `,
  transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
  textTransform: 'none' as const,
  fontWeight: 600,
  position: 'relative' as const,
  overflow: 'hidden',
  '&::before': {
    ...shimmerBefore,
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
  },
  '&:hover': {
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: `
      0 6px 16px rgba(${rgb}, 0.5),
      0 0 0 1px rgba(255, 255, 255, 0.2)
    `,
    '&::before': { left: '100%' }
  },
  '& .MuiButton-startIcon': { zIndex: 2 },
  '& .MuiButton-label': { zIndex: 2 }
});

const AdminPanel: React.FC = () => {
  // Read role/username from localStorage ONCE per render instead of scattering
  // localStorage.getItem() calls throughout the JSX.
  const role = localStorage.getItem('role');
  const username = localStorage.getItem('username')?.toLowerCase();

  // Precomputed once so TS doesn't try to narrow `role` across comparisons
  // to different literals within the same expression (which throws a
  // "no overlap" error even though role can plainly hold either value).
  const isAdmin = role === 'admin';
  const isView = role === 'view';
  const isGuide = role === 'guide';
  const isKm = role === 'km';
  const isTo = role === 'to';
  const isFullReport = role === 'full-report';
  const isModer = role === 'moder';
  const isAdminUser = username === 'admin';

  const [selectedIndex, setSelectedIndex] = useState(
    (isGuide || isFullReport)
      ? 10
      : (isKm || isTo) ? 2 : 3
  );
  const [reportsExpanded, setReportsExpanded] = useState(false);
  const [documentsExpanded, setDocumentsExpanded] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [helpOpen, setHelpOpen] = useState(
    isGuide || isKm || isTo || isFullReport
  );
  const [selectedReportModule, setSelectedReportModule] = useState<ReportModule | undefined>(undefined);
  const [usernameState, setUsernameState] = useState('');
  const [currentRole, setCurrentRole] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isSidebarOpen] = useState(true);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    index: number;
    module?: ReportModule;
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Панель администратора | Zerde';
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const module = params.get('module') as ReportModule | null;

    if (tab) {
      const tabNum = Number(tab);
      if (tabNum === 107 && !isAdmin) {
        // Доступ к уведомлениям только для admin
      } else {
        setSelectedIndex(tabNum);
        if (tabNum === 103 || tabNum === 106) {
          setDocumentsExpanded(true);
        }
        if (tabNum === 7 || tabNum === 100 || tabNum === 101 || tabNum === 102 || tabNum === 104 || tabNum === 107) {
          setAdminExpanded(true);
        }
      }
    }
    if (module) {
      setSelectedReportModule(module);
    }
  }, [isAdmin]);

  const fetchUserRole = async (usernameToFetch: string) => {
    try {
      const response = await axios.get('https://tbl.zerdegroup.kz/api/user-role', {
        params: { username: usernameToFetch }
      });
      if (response.data.success) {
        setCurrentRole(response.data.role);
      } else {
        setError('Ошибка получения роли: ' + response.data.message);
        setCurrentRole('');
      }
    } catch (error) {
      console.error('Ошибка при получении роли пользователя:', error);
      setError('Ошибка при получении данных');
      setCurrentRole('');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userId');
    localStorage.removeItem('role');
    localStorage.removeItem('username');
    navigate('/');
  };

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    if (storedUsername) {
      setUsernameState(storedUsername);
      fetchUserRole(storedUsername);
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const menuItems = [
    { index: 0, icon: <PersonAdd />, label: 'Создать пользователя' },
    { index: 2, icon: <PeopleAlt />, label: 'Пользователи' },
    { index: 3, icon: <Folder />, label: 'Отчеты' },
    { index: 4, label: 'Таблица', icon: <TableChart /> },
    { index: 5, label: 'Логи', icon: <FeaturedPlayList /> },
    { index: 6, label: 'Мат. отчет', icon: <FeaturedPlayList /> },
    { index: 103, label: 'Документы', icon: <DescriptionIcon /> },
    { index: 105, label: 'Электронный журнал', icon: <Assignment /> },
    { index: 200, label: 'Админ', icon: <AdminPanelSettings /> }
  ];

  const helpItems = [
    { index: 10, label: 'Остатки', icon: <Warehouse /> },
    { index: 11, label: 'Справочник', icon: <MenuBook /> }
  ];

  const handleOpenInNewTab = (index: number, module?: ReportModule) => {
    const url = window.location.href.split('?')[0] + `?tab=${index}${module ? `&module=${module}` : ''}`;
    window.open(url, '_blank');
  };

  const handleContextMenu = (event: React.MouseEvent, index: number, module?: ReportModule) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      index,
      module
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleReportsClick = () => {
    setSelectedIndex(3);
    setSelectedReportModule(undefined);
  };

  const handleReportsExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setReportsExpanded(!reportsExpanded);
  };

  const handleDocumentsClick = () => {
    setSelectedIndex(103);
    setDocumentsExpanded(!documentsExpanded);
  };

  const handleDocumentsExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setDocumentsExpanded(!documentsExpanded);
  };

  const handleAdminExpandClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAdminExpanded(!adminExpanded);
  };

  const handleHelpClick = () => {
    setHelpOpen(!helpOpen);
  };

  const handleReportModuleClick = (module: ReportModule) => {
    setSelectedIndex(3);
    setSelectedReportModule(module);
  };

  const getPageTitle = () => {
    const menuItem = menuItems.find(item => item.index === selectedIndex);
    if (menuItem) {
      return menuItem.label;
    }

    // Заголовки для админских модулей
    switch (selectedIndex) {
      case 7:
        return 'Планировщик';
      case 10:
        return 'Остатки';
      case 11:
        return 'Справочник';
      case 100:
        return 'Опасные функции';
      case 101:
        return 'Табель РБ';
      case 102:
        return 'Данные ОФД';
      case 103:
        return 'Документооборот';
      case 104:
        return 'WhatsApp Боты';
      case 105:
        return 'Электронный журнал';
      case 106:
        return 'КМ\'ы';
      case 107:
        return 'Центр уведомлений';
      default:
        return '';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', position: 'relative', overflow: 'hidden' }}>
      <Paper
        elevation={24}
        sx={{
          width: '250px',
          flexShrink: 0,
          background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(99, 102, 241, 0.2)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10,
          margin: 2,
          borderRadius: '24px',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'hidden',
          boxShadow: `
            0 20px 50px rgba(0, 0, 0, 0.3),
            0 8px 16px rgba(99, 102, 241, 0.2),
            inset 0 1px 0 rgba(99, 102, 241, 0.1)
          `
        }}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          p: 2
        }}>
          {isSidebarOpen && (
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                opacity: isSidebarOpen ? 1 : 0,
                maxWidth: '100%'
              }}
            >
              <img src={logo} style={{ maxWidth: '220px', margin: "auto" }} alt="logo" />
            </Typography>
          )}
        </Box>

        <List sx={{
          flexGrow: 1,
          maxHeight: 'calc(100% - 140px)',
          overflowY: 'auto',
          paddingTop: 0,
          paddingBottom: 0
        }}>
          {menuItems.map((item) => {
            // Для роли 'guide' скрываем все основные пункты меню, кроме 'Пользователи' (index 2) и 'Документы' (index 103)
            if (isGuide && item.index !== 2 && item.index !== 103) {
              return null;
            }

            // Для роли 'km': только Пользователи (2) и Электронный журнал (105)
            if (isKm && item.index !== 2 && item.index !== 105) {
              return null;
            }

            // Для роли 'to': только Пользователи (2), Документы (103) и Электронный журнал (105)
            if (isTo && item.index !== 2 && item.index !== 103 && item.index !== 105) {
              return null;
            }

            // Для роли 'full-report': только Отчеты (3), Документы (103) и Электронный журнал (105)
            if (isFullReport && item.index !== 3 && item.index !== 103 && item.index !== 105) {
              return null;
            }

            if (
              (item.index === 5 &&
                !(isAdmin || (username === 'galiya' && isModer))) ||
              (item.index === 4 &&
                (!isAdmin || !isAdminUser)) ||
              item.index === 6 ||
              (item.index === 0 && (isView || isKm || isTo)) ||
              (item.index === 200 && !isAdmin) ||
              (item.index === 103 &&
                !(isAdmin || isGuide || isTo || isFullReport))
            ) {
              return null;
            }

            if (item.index === 10) {
              return null; // Справка теперь в выпадающем меню
            }

            if (item.index === 3) {
              return (
                <React.Fragment key={item.index}>
                  <ListItemButton onClick={handleReportsClick} sx={parentItemSx}>
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                    <Box onClick={handleReportsExpandClick} sx={{ cursor: 'pointer' }}>
                      {reportsExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </ListItemButton>
                  <Collapse in={reportsExpanded} timeout="auto" unmountOnExit sx={collapseSx}>
                    <List component="div" disablePadding>
                      {!isFullReport && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => handleReportModuleClick('empty')}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'empty'); }}
                          onContextMenu={(e) => handleContextMenu(e, 3, 'empty')}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <PermContactCalendarIcon />
                          </ListItemIcon>
                          <ListItemText primary="Заполнение" />
                        </ListItemButton>
                      )}
                      {!isView && !isFullReport && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => handleReportModuleClick('edit')}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'edit'); }}
                          onContextMenu={(e) => handleContextMenu(e, 3, 'edit')}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <DriveFileRenameOutlineIcon />
                          </ListItemIcon>
                          <ListItemText primary="Редактирование" />
                        </ListItemButton>
                      )}
                      {!isView && !isFullReport && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => handleReportModuleClick('editmonth')}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'editmonth'); }}
                          onContextMenu={(e) => handleContextMenu(e, 3, 'editmonth')}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <CalendarMonth />
                          </ListItemIcon>
                          <ListItemText primary="Ред. мес. отчет" />
                        </ListItemButton>
                      )}
                      {(!isView || isAdmin) && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => handleReportModuleClick('view')}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'view'); }}
                          onContextMenu={(e) => handleContextMenu(e, 3, 'view')}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <DescriptionIcon />
                          </ListItemIcon>
                          <ListItemText primary="Отчет" />
                        </ListItemButton>
                      )}
                      {(isView || isAdmin) && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => handleReportModuleClick('editnew')}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'editnew'); }}
                          onContextMenu={(e) => handleContextMenu(e, 3, 'editnew')}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <DescriptionIcon />
                          </ListItemIcon>
                          <ListItemText primary="Дир отчет" />
                        </ListItemButton>
                      )}
                      <ListItemButton
                        sx={subItemSx}
                        onClick={() => handleReportModuleClick('compare')}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'compare'); }}
                        onContextMenu={(e) => handleContextMenu(e, 3, 'compare')}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <TableChart />
                        </ListItemIcon>
                        <ListItemText primary="Доставка" />
                      </ListItemButton>
                      {(!isView || isAdmin) && !isFullReport && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => handleReportModuleClick('MathReport')}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(3, 'MathReport'); }}
                          onContextMenu={(e) => handleContextMenu(e, 3, 'MathReport')}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <RequestPage />
                          </ListItemIcon>
                          <ListItemText primary="Мат отчет" />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            if (item.index === 200) {
              return (
                <React.Fragment key={item.index}>
                  <ListItemButton onClick={handleAdminExpandClick} sx={parentItemSx}>
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                    {adminExpanded ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={adminExpanded} timeout="auto" unmountOnExit sx={collapseSx}>
                    <List component="div" disablePadding>
                      <ListItemButton
                        sx={subItemSx}
                        onClick={() => setSelectedIndex(7)}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(7); }}
                        onContextMenu={(e) => handleContextMenu(e, 7)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <ManageHistory />
                        </ListItemIcon>
                        <ListItemText primary="Планировщик" />
                      </ListItemButton>
                      <ListItemButton
                        sx={subItemSx}
                        onClick={() => setSelectedIndex(100)}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(100); }}
                        onContextMenu={(e) => handleContextMenu(e, 100)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <Cancel />
                        </ListItemIcon>
                        <ListItemText primary="Опасные функции" />
                      </ListItemButton>
                      <ListItemButton
                        sx={subItemSx}
                        onClick={() => setSelectedIndex(101)}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(101); }}
                        onContextMenu={(e) => handleContextMenu(e, 101)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <AddToDrive />
                        </ListItemIcon>
                        <ListItemText primary="Табель РБ" />
                      </ListItemButton>
                      <ListItemButton
                        sx={subItemSx}
                        onClick={() => setSelectedIndex(102)}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(102); }}
                        onContextMenu={(e) => handleContextMenu(e, 102)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <Blinds />
                        </ListItemIcon>
                        <ListItemText primary="Данные ОФД" />
                      </ListItemButton>

                      {/* WhatsApp Боты - только для username === 'admin' */}
                      {isAdminUser && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => setSelectedIndex(104)}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(104); }}
                          onContextMenu={(e) => handleContextMenu(e, 104)}
                        >
                          <ListItemIcon sx={{ color: '#25D366', minWidth: 36 }}>
                            <MarkAsUnread />
                          </ListItemIcon>
                          <ListItemText primary="WhatsApp Боты" />
                        </ListItemButton>
                      )}

                      {/* Уведомления - только для роли admin */}
                      {isAdmin && (
                        <ListItemButton
                          sx={subItemSx}
                          onClick={() => setSelectedIndex(107)}
                          onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(107); }}
                          onContextMenu={(e) => handleContextMenu(e, 107)}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                            <CampaignIcon />
                          </ListItemIcon>
                          <ListItemText primary="Уведомления" />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            if (item.index === 103) {
              return (
                <React.Fragment key={item.index}>
                  <ListItemButton
                    onClick={handleDocumentsClick}
                    sx={{
                      ...parentItemSx,
                      ...(selectedIndex === 103 || selectedIndex === 106
                        ? { bgcolor: 'rgba(99, 102, 241, 0.25)', color: '#ffffff' }
                        : {})
                    }}
                  >
                    <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.label} />
                    <Box onClick={handleDocumentsExpandClick} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                      {documentsExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  </ListItemButton>
                  <Collapse in={documentsExpanded} timeout="auto" unmountOnExit sx={collapseSx}>
                    <List component="div" disablePadding>
                      <ListItemButton
                        sx={{
                          ...subItemSx,
                          ...(selectedIndex === 103
                            ? { bgcolor: 'rgba(99, 102, 241, 0.35)', color: '#ffffff' }
                            : {})
                        }}
                        onClick={() => setSelectedIndex(103)}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(103); }}
                        onContextMenu={(e) => handleContextMenu(e, 103)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <DescriptionIcon />
                        </ListItemIcon>
                        <ListItemText primary="Документооборот" />
                      </ListItemButton>
                      <ListItemButton
                        sx={{
                          ...subItemSx,
                          ...(selectedIndex === 106
                            ? { bgcolor: 'rgba(99, 102, 241, 0.35)', color: '#ffffff' }
                            : {})
                        }}
                        onClick={() => setSelectedIndex(106)}
                        onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(106); }}
                        onContextMenu={(e) => handleContextMenu(e, 106)}
                      >
                        <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                          <QrCodeIcon />
                        </ListItemIcon>
                        <ListItemText primary="КМ'ы" />
                      </ListItemButton>
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            return (
              <ListItemButton
                key={item.index}
                selected={selectedIndex === item.index}
                onClick={() => setSelectedIndex(item.index)}
                onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(item.index); }}
                onContextMenu={(e) => handleContextMenu(e, item.index)}
                sx={parentItemSx}
              >
                {item.icon && (
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  sx={{ opacity: isSidebarOpen ? 1 : 0 }}
                />
              </ListItemButton>
            );
          })}

          <ListItemButton onClick={handleHelpClick} sx={parentItemSx}>
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <Analytics />
            </ListItemIcon>
            <ListItemText primary="Справка" />
            {helpOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={helpOpen} timeout="auto" unmountOnExit sx={collapseSx}>
            <List component="div" disablePadding>
              {helpItems.map((item) => (
                <ListItemButton
                  key={item.index}
                  selected={selectedIndex === item.index}
                  onClick={() => setSelectedIndex(item.index)}
                  onAuxClick={(e) => { if (e.button === 1) handleOpenInNewTab(item.index); }}
                  onContextMenu={(e) => handleContextMenu(e, item.index)}
                  sx={subItemSx}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </List>

        <Box sx={{ p: 1.5 }}>
          <Button
            sx={actionButtonSx('#06b6d4', '#0891b2', '6, 182, 212')}
            variant="contained"
            fullWidth
            onClick={() => window.open('https://mail.zerdegroup.kz/', '_blank', 'noopener,noreferrer')}
            startIcon={<MarkAsUnread />}
          >
            <Box sx={{ zIndex: 2 }}>Почта</Box>
          </Button>

          <Button
            sx={actionButtonSx('#10b981', '#059669', '16, 185, 129')}
            variant="contained"
            fullWidth
            onClick={() => window.open('https://guide.zerdegroup.kz/', '_blank', 'noopener,noreferrer')}
            startIcon={<MenuBook />}
          >
            <Box sx={{ zIndex: 2 }}>База знаний Zerde</Box>
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{ ...actionButtonSx('#ef4444', '#dc2626', '239, 68, 68'), mb: 0, minWidth: 0 }}
          >
            <Box sx={{ zIndex: 2 }}>{isSidebarOpen ? 'Выйти' : ''}</Box>
          </Button>
        </Box>
      </Paper>

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <Paper
          elevation={20}
          sx={{
            m: 2,
            background: 'linear-gradient(145deg, rgba(255,255,255,0.98), rgba(255,255,255,0.92))',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255,255,255,0.4)',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            height: '75vh',
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.12),
              0 2px 8px rgba(99, 102, 241, 0.08),
              inset 0 1px 0 rgba(255,255,255,0.6)
            `
          }}
        >
          <Box sx={{
            p: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'stretch',
            borderBottom: '1px solid rgba(0,0,0,0.1)'
          }}>
            <Typography
              variant="h4"
              sx={{
                color: 'black',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              {getPageTitle()}
            </Typography>
            <Typography sx={{ color: 'black' }}>
              Логин: {usernameState} <br /> Роль: {currentRole || 'Загрузка...'}
            </Typography>
          </Box>
          <Box sx={{
            flexGrow: 1,
            p: 3,
            overflow: 'auto'
          }}>
            {selectedIndex === 0 && (
              <CreateAccount
                username={usernameState}
                currentRole={currentRole}
                loading={loading}
              />
            )}
            {selectedIndex === 2 && <AllUsers />}
            {selectedIndex === 3 && (
              <>
                {!selectedReportModule && <CheckUsersData setSelectedIndex={setSelectedIndex} />}
                {selectedReportModule === 'empty' && <UsersPerDay />}
                {selectedReportModule === 'edit' && <EditReportModule />}
                {selectedReportModule === 'editmonth' && <EditMonthReportModule />}
                {selectedReportModule === 'editnew' && <ViewReportModuleDir />}
                {selectedReportModule === 'view' && <ViewReportModule />}
                {selectedReportModule === 'compare' && <Compare />}
                {selectedReportModule === 'MathReport' && <MathReport setSelectedIndex={setSelectedIndex} />}
              </>
            )}
            {selectedIndex === 4 && usernameState === 'admin' && <BDTable setSelectedIndex={setSelectedIndex} />}
            {selectedIndex === 5 && <ConsoleLog />}
            {selectedIndex === 6 && <MathReport setSelectedIndex={setSelectedIndex} />}
            {selectedIndex === 7 && isAdmin && <TaskManager />}
            {selectedIndex === 10 && <PharmacyMap />}
            {selectedIndex === 11 && <Nomenclature />}
            {selectedIndex === 100 && isAdmin && <Dangerous />}
            {selectedIndex === 101 && isAdmin && <TabelRB />}
            {selectedIndex === 102 && isAdmin && <OFDManagement />}
            {selectedIndex === 103 && <AdminDocuments />}
            {selectedIndex === 104 && isAdminUser && <WhatsAppBots />}
            {selectedIndex === 105 && <AdminJournal />}
            {selectedIndex === 106 && <AdminMarkingCodes />}
            {selectedIndex === 107 && isAdmin && <AdminNotifications />}
          </Box>
        </Paper>
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={() => {
          if (contextMenu) {
            handleOpenInNewTab(contextMenu.index, contextMenu.module);
          }
          handleCloseContextMenu();
        }}>
          Открыть в новой вкладке
        </MenuItem>
      </Menu>

      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setError(null)}
            severity="error"
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      )}

      <Box
        sx={{
          backgroundImage: `url(${bg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          width: '105%',
          height: '105vh',
          position: 'fixed',
          top: -15,
          left: -15,
          filter: 'blur(8px) brightness(0.4)',
          zIndex: -1
        }}
      />
    </Box>
  );
};

export default AdminPanel;